import React, { useCallback, useContext, useEffect, useState } from "react";
import { Checkbox, Col, DatePicker, Modal, Row, Table } from "antd";
import {
  CancelButton,
  PrimaryButton,
  SecondaryButton,
} from "../CustomButtons/CustomButtons";
import { GlobalContext } from "contexts/GlobalContext";
import PreLoadPagesSpin from "components/PreLoadPagesSpin";
import {
  fetchFilterShortCutsForVolSurface,
  sendSftpFile,
} from "api/volSurfaceApi";
import moment from "moment";
import { getDefaultDate, isAdmin, toast } from "helpers/generalHelper";

const SendSFTPFile = () => {
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [shortcuts, setShortcuts] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [date, setDate] = useState(null);
  const [selectedShortcuts, setSelectedShortcuts] = useState([]);
  const [selectedSendType, setSelectedSendType] = useState([
    "vol_surface",
    "audit_trail",
  ]);
  const [isShortcutsLoading, setIsShortcutsLoading] = useState(false);

  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  const formatDate = (date) => {
    return moment(date).format("YYYY-MM-DD");
  };

  const handleSendEmail = () => {
    setIsSendLoading(true);
    sendSftpFile({
      shortcuts: selectedShortcuts,
      send_types: selectedSendType,
      date: formatDate(date),
    })
      .then((res) => {
        toast(res?.data?.message, "success");
        //wait for 1 second
        setTimeout(() => {
          setIsSendLoading(false);
          setShowSendModal(false);
        }, 1000);
      })
      .catch((err) => {
        setIsSendLoading(false);
        console.log("Error >>", err);
      });
  };

  const getShortcuts = useCallback(() => {
    setIsShortcutsLoading(true);
    fetchFilterShortCutsForVolSurface({
      is_x_axis_delta: 1,
      date: formatDate(date),
    })
      .then((res) => {
        setShortcuts(
          res.data?.data?.sort((a, b) => a?.label?.localeCompare(b?.label))
        );
        const selectedShortcuts = res.data?.data
          ?.filter((item) => item.selected)
          .map((item) => item.product_contract_style_id);
        setSelectedShortcuts(selectedShortcuts);
        setIsShortcutsLoading(false);
      })
      .finally(() => {
        setIsShortcutsLoading(false);
      });
  }, [date]);

  useEffect(() => {
    if (date) {
      getShortcuts();
    }
  }, [date, getShortcuts]);

  const handleSendTypeChange = (checkedValues) => {
    setSelectedSendType(checkedValues);
  };

  const handleDateChange = (date) => {
    if (date) {
      setDate(date);
    } else {
      setDate(getDefaultDate());
    }
  };

  function onCancel() {
    setShowSendModal(false);
    setSelectedShortcuts([]);
    setDate(null);
  }

  const isAdminUser = isAdmin();

  const disabledDate = (current) => {
    if (!current) return false;
    if (isAdminUser) {
      // Admin: allow today and past; disable future
      return current.isAfter(moment().endOf("day"));
    }
    // Non-admin: allow only yesterday
    const yStart = moment().subtract(1, "day").startOf("day");
    const yEnd = moment().subtract(1, "day").endOf("day");
    return !current.isBetween(yStart, yEnd, "day", "[]");
  };

  return (
    <>
      <Col span={24}>
        <Row className="bias-btn">
          <Col span={3}>Send to Clients:</Col>
          <Col>
            <SecondaryButton
              onClick={() => {
                setShowSendModal(true);
                setDate(getDefaultDate());
              }}
            >
              Send
            </SecondaryButton>
          </Col>
        </Row>
      </Col>
      <Modal
        width={"50%"}
        className={`${dark_Theme && "ant-modal-dark content_dark"}`}
        title="Send to Clients Confirmation"
        style={{
          top: 20,
        }}
        confirmLoading={isSendLoading}
        open={showSendModal}
        onCancel={onCancel}
        centered
        footer={[
          <PrimaryButton
            disabled={
              isSendLoading ||
              selectedShortcuts?.length === 0 ||
              !date ||
              selectedSendType?.length === 0
            }
            loading={isSendLoading}
            tooltip={
              !date
                ? "Please select a date"
                : selectedShortcuts?.length === 0
                ? "Please select shortcuts"
                : selectedSendType?.length === 0
                ? "Please select files to send"
                : null
            }
            key="send"
            onClick={() => handleSendEmail()}
          >
            Send
          </PrimaryButton>,
          <CancelButton key="cancel" onClick={onCancel}>
            Cancel
          </CancelButton>,
        ]}
      >
        <div className="d-flex flex-column gap-3">
          <Row>
            <Col span={5}>
              <p className="fw-bold mb-1">Send Date:</p>
            </Col>
            <Col span={11} className={` ${dark_Theme && "date-picker-dark"}`}>
              <DatePicker
                style={{ width: "200px" }}
                placeholder="Select Date"
                popupClassName={dark_Theme && "date-picker-dropdown-dark"}
                onChange={handleDateChange}
                value={date}
                showNow={false}
                disabledDate={disabledDate}
                allowClear={false}
              />
            </Col>
          </Row>
          <Row className="mt-1">
            <Col span={5}>
              <p className="fw-bold mb-1">Files to Send:</p>
            </Col>
            <Col span={11}>
              <Checkbox.Group
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "10px",
                  color: "white",
                }}
                options={[
                  { label: "Vol Surface", value: "vol_surface" },
                  { label: "Audit Trail", value: "audit_trail" },
                ]}
                defaultValue={["vol_surface", "audit_trail"]}
                onChange={handleSendTypeChange}
              />
            </Col>
          </Row>
        </div>
        {isShortcutsLoading ? (
          <PreLoadPagesSpin
            className={"other-info--container--spin"}
            size={"large"}
            dark_Theme={dark_Theme}
          />
        ) : (
          <>
            <p className="fw-bold mt-4 mb-2">Select Shortcuts:</p>
            <Table
              pagination={false}
              bordered
              size="small"
              className={`${dark_Theme && "table-dark"}`}
              rowKey={(record) => record?.product_contract_style_id}
              dataSource={shortcuts?.map((item) => ({
                ...item,
                key: item?.product_contract_style_id,
              }))}
              rowSelection={{
                selectedRowKeys: selectedShortcuts,
                type: "checkbox",
                onChange: (selectedRowKeys, selectedRows) => {
                  setSelectedShortcuts(selectedRowKeys);
                },
              }}
              columns={[
                {
                  title: "Shortcut",
                  dataIndex: "label",
                  key: "label",
                },
                {
                  title: "Last EOD Surface",
                  dataIndex: "last_eod_surface",
                  key: "last_eod_surface",
                },
              ]}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default SendSFTPFile;
